/**
 * §P.6 · Los avisos del club.
 *
 * ## Publicar es interrumpir a 120 familias
 *
 * Es la operación que más gente toca de golpe en todo el producto, y por eso:
 *
 * - **El alcance se dice antes de publicar**, no después. «Llega a 34 familias» es
 *   la cifra que frena un aviso mandado a todo el club por error, y va en el propio
 *   botón (`POST /announcements/preview`). Hasta el 13-10-2026 la pantalla lo
 *   prometía sin que nada lo calculara.
 * - **`reach` cuenta familias, no alumnos.** Dos hermanos en el mismo grupo son una
 *   familia y un teléfono. Contar alumnos daría un número más halagador y mentiría
 *   sobre lo único que importa: a cuánta gente se interrumpe.
 * - **El borrador existe** y se ofrece al lado del botón de publicar. Y se publica
 *   después, desde la lista (`POST /announcements/{id}/publish`): antes del
 *   13-10-2026 un borrador ya solo se podía corregir o retirar.
 *
 * ## Las audiencias se suman
 *
 * «Los del martes» y «los cinturones verdes» llega a la unión, no a la
 * intersección: es como lo dice un maestro en voz alta, y es lo contrario de lo que
 * sugiere un formulario con dos filtros. La pantalla lo escribe.
 *
 * ## Corregir no cambia a quién llegó
 *
 * `PATCH` no acepta audiencia, y no es un olvido del contrato: quien ya lo recibió
 * no puede des-recibirlo, y quien no lo recibió no se enteraría del cambio. Para
 * eso está retirarlo y publicar otro, y la pantalla lo dice con esas palabras.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  estaCaducado,
  estaPublicado,
  etiquetaDeAudiencia,
  porcentajeLeido,
  type Aviso,
} from '@/club/avisos/tipos';
import { FormularioDeAviso } from '@/club/avisos/FormularioDeAviso';

export function Avisos({
  club,
  puedeEscribir,
  puedeCorregir,
  suspendido,
  alVerAcuses,
  empezarEscribiendo = false,
}: {
  club: string;
  /** Con un aviso nuevo ya abierto: «Escribir un aviso» de Inicio. */
  empezarEscribiendo?: boolean;
  /** Publicar sí lo puede el entrenador: es quien avisa de que hoy no hay clase. */
  puedeEscribir: boolean;
  /** Corregir y retirar, solo el dueño y el administrativo. */
  puedeCorregir: boolean;
  suspendido: boolean;
  alVerAcuses: (avisoId: string) => void;
}) {
  const cache = useQueryClient();
  // `null` es un aviso nuevo; `undefined`, ninguno abierto.
  const [escribiendo, setEscribiendo] = useState<Aviso | null | undefined>(
    empezarEscribiendo ? null : undefined
  );
  const [retirando, setRetirando] = useState<Aviso | null>(null);
  /*
   * El borrador que se va a publicar, con la clave de idempotencia de esta
   * confirmación: el doble clic sobre «Sí, publicar» es justo el que no puede
   * mandar dos avisos a cada familia.
   */
  const [publicando, setPublicando] = useState<{
    aviso: Aviso;
    clave: string;
  } | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const avisos = useQuery({
    queryKey: ['avisos', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).announcements.get({
        // Los borradores y los caducados vienen siempre: un borrador que no se ve
        // es un aviso que alguien dio por mandado.
        query: { include_drafts: true, include_expired: true, limit: 100 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Aviso[];
    },
  });

  const publicar = useMutation({
    mutationFn: async ({ aviso, clave }: { aviso: Aviso; clave: string }) => {
      const r = await api.v1
        .clubs({ club })
        .announcements({ announcementId: aviso.id })
        .publish.post(undefined, { headers: { 'idempotency-key': clave } });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setPublicando(null);
      void cache.invalidateQueries({ queryKey: ['avisos', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        .announcements({ announcementId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRetirando(null);
      void cache.invalidateQueries({ queryKey: ['avisos', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (escribiendo !== undefined) {
    return (
      <FormularioDeAviso
        club={club}
        aviso={escribiendo}
        alTerminar={() => {
          setEscribiendo(undefined);
          void cache.invalidateQueries({ queryKey: ['avisos', club] });
        }}
        alCancelar={() => setEscribiendo(undefined)}
      />
    );
  }

  // ── Publicar un borrador, con la cifra delante ───────────────────────────
  if (publicando) {
    const { aviso } = publicando;
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Publicar «{aviso.title}»?</h3>
        <p>
          Llega <strong>ahora mismo</strong> al móvil de{' '}
          {aviso.reach === 1 ? 'una familia' : `${aviso.reach} familias`}.
          Después se puede corregir el texto, pero no a quién llegó.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-accion">
          <button
            type="button"
            disabled={publicar.isPending}
            onClick={() => {
              setProblema(null);
              publicar.mutate(publicando);
            }}
          >
            {publicar.isPending ? 'Publicando…' : 'Sí, publicar y avisar'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={publicar.isPending}
            onClick={() => {
              setPublicando(null);
              setProblema(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  // ── Retirar, con la pregunta delante ─────────────────────────────────────
  if (retirando) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Retirar «{retirando.title}»?</h3>

        <ul className="lista-clara">
          <li>
            <strong>Quien ya lo recibió lo tiene recibido.</strong> Retirarlo lo
            quita del muro y de la app, pero el aviso llegó a{' '}
            {retirando.reach === 1
              ? 'una familia'
              : `${retirando.reach} familias`}{' '}
            y eso no se deshace.
          </li>
          <li>
            Si lo que hay es una errata, <strong>corrígelo</strong>: cambiar el
            texto sí llega a quien ya lo tiene.
          </li>
        </ul>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={retirar.isPending}
            onClick={() => retirar.mutate(retirando.id)}
          >
            {retirar.isPending ? 'Retirando…' : 'Sí, retirarlo'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setRetirando(null);
              setProblema(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Avisos</h1>
        {puedeEscribir && !suspendido && (
          <button type="button" onClick={() => setEscribiendo(null)}>
            Escribir un aviso
          </button>
        )}
      </header>

      <p className="tenue">
        Lo que se publica aquí llega al móvil de las familias. Cada aviso dice a
        cuántas llegó y cuántas lo han leído.
      </p>

      {avisos.isPending && <Cargando filas={4} etiqueta="Cargando avisos" />}

      {avisos.isError && (
        <PanelError
          problema={aProblema(avisos.error)}
          reintentar={() => void avisos.refetch()}
        />
      )}

      {problema && <PanelError problema={problema} />}

      {avisos.data?.length === 0 && (
        <Vacio
          titulo="Todavía no hay avisos"
          explicacion="Un aviso llega al móvil de las familias que elijas: todo el club, un grupo, un cinturón o quien debe dinero."
          {...(puedeEscribir && !suspendido
            ? {
                accion: (
                  <button type="button" onClick={() => setEscribiendo(null)}>
                    Escribir el primero
                  </button>
                ),
              }
            : {})}
        />
      )}

      {avisos.data && avisos.data.length > 0 && (
        <ul className="avisos">
          {avisos.data.map((a) => {
            const publicado = estaPublicado(a);
            const caducado = estaCaducado(a);
            const leido = porcentajeLeido(a);

            return (
              <li
                key={a.id}
                className={publicado && !caducado ? undefined : 'fila-fuera'}
              >
                <div className="aviso-cabeza">
                  <strong>{a.title}</strong>
                  {a.pinned && (
                    <span className="pastilla pastilla-info">Fijado</span>
                  )}
                  {!publicado && (
                    <span className="pastilla pastilla-paused">Borrador</span>
                  )}
                  {caducado && <span className="pastilla">Caducado</span>}
                  {a.requires_ack && (
                    <span className="pastilla pastilla-info">
                      Pide confirmación
                    </span>
                  )}
                </div>

                <p className="aviso-cuerpo">{a.body}</p>

                {/* A quién fue: es lo que se mira cuando algo se mandó mal. */}
                <div className="aviso-audiencias">
                  {a.audiences.map((aud, i) => (
                    <span key={i} className="pastilla pastilla-left">
                      {etiquetaDeAudiencia(aud)}
                    </span>
                  ))}
                </div>

                <div className="aviso-pie">
                  <span className="tenue">
                    {publicado
                      ? `Publicado ${fechaYHora(a.published_at)}`
                      : 'Sin publicar'}
                    {a.author_name ? ` · ${a.author_name}` : ''}
                  </span>

                  {!publicado && (
                    <span className="tenue">
                      {a.reach === 0
                        ? 'No llegaría a nadie'
                        : `Llegaría a ${a.reach === 1 ? 'una familia' : `${a.reach} familias`}`}
                    </span>
                  )}

                  {!publicado &&
                    puedeEscribir &&
                    !suspendido &&
                    a.reach > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setProblema(null);
                          setPublicando({
                            aviso: a,
                            clave: crypto.randomUUID(),
                          });
                        }}
                      >
                        Publicar y avisar
                      </button>
                    )}

                  {publicado && (
                    <button
                      type="button"
                      className="enlace-fila"
                      onClick={() => alVerAcuses(a.id)}
                    >
                      {/*
                        La cifra que importa no es cuántos lo leyeron sino que se
                        pueda ver **quién no**: es la que dice a quién llamar.
                      */}
                      {a.reach === 0
                        ? 'No llegó a nadie'
                        : leido === null
                          ? `${a.reach} familias`
                          : `${a.read_count} de ${a.reach} lo han leído · ${leido} %`}
                    </button>
                  )}

                  {puedeCorregir && !suspendido && (
                    <span className="tutor-acciones" style={{ margin: 0 }}>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => setEscribiendo(a)}
                      >
                        Corregir
                      </button>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setRetirando(a);
                          setProblema(null);
                        }}
                      >
                        Retirar
                      </button>
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
